import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EntryProtocol} from 'notebook/ELNConstants';
import EntryExportConfig, {EntryExportConfigShape} from 'notebook/entries/EntryExportConfig';
import EntryExportMetadata from 'notebook/entries/EntryExportMetadata';
import {
  editorPropertiesContextForPrintView,
  getExportConfigForMetadataSection,
  getPrinterFriendlySectionConfigurationContext,
} from 'notebook/entries/PrinterFriendlyViewUtils';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {EditorPropertiesContext, SectionConfigurationContext} from 'notebook/notes/core-ui/NoteContext';
import {CidIdMap} from 'notebook/notes/NoteConstants';
// @ts-expect-error
import ProtocolEditor from 'notebook/protocols/editor/Editor';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import StageEntryNoteView from 'workflows/stage-entries/StageEntryNoteView';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import {StageEntryDocument} from 'workflows/WorkflowConstants';

interface EnhancedProps {
  editorState: {
    stageEntryDocument: StageEntryDocument;
    cidIdMap: CidIdMap;
    isLatest: boolean;
  };
}

class StageEntryPrintViewConnector extends DataConnector<BaseStageEntryPrintViewProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [StageEntryStore];

  enhanceProps(): EnhancedProps {
    return {editorState: this.stageEntryStore().getEditorState(this.props.id)};
  }
}

interface BaseStageEntryPrintViewProps {
  id: string;
  exportConfig?: EntryExportConfig;
  showProtocols: Array<number>;
  indexSlice: {start: number; end: number};
}

export type StageEntryPrintViewProps = BaseStageEntryPrintViewProps & EnhancedProps;

class StageEntryPrintView extends React.Component<StageEntryPrintViewProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    id: PropTypes.string.isRequired,
    exportConfig: PropTypes.shape(EntryExportConfigShape),
    showProtocols: PropTypes.array.isRequired,
    indexSlice: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }),
    editorState: PropTypes.object.isRequired,
  };

  renderEditor(): JSX.Element | null {
    const {stageEntryDocument, cidIdMap, isLatest} = this.props.editorState;
    const shouldRender = (protocol: EntryProtocol): boolean =>
      this.props.showProtocols.includes(cidIdMap[protocol.cid] as number) && protocol.externalFile == null;
    if (stageEntryDocument == null) {
      return <HorizontalThrobber />;
    }

    let {exportConfig} = this.props;
    // Show the title by default unless explicitly disabled in the export config
    const showTitle = exportConfig?.showEntryTitle ?? true;

    // Show the first section header unless the export config is asking for us not to do so.
    // To hide the section we mark the preamble as being true in `getPrinterFriendlySectionConfigurationContext`.
    // This then causes the same behavior that we have for Documents where we don't show the first SectionHeader.
    const showFirstSectionHeader = exportConfig?.showFirstSectionHeader ?? true;
    const sectionConfiguration = getPrinterFriendlySectionConfigurationContext(!showFirstSectionHeader);

    // If the metadata section in print preview is enabled, populate/update the related exportConfig props.
    if (BenchlingConfig.ELN_RENDER_PRINT_PREVIEW_METADATA) {
      exportConfig = getExportConfigForMetadataSection(exportConfig);
    }

    let beforeEntry;
    if (showTitle) {
      beforeEntry = (
        <div>
          {exportConfig && <EntryExportMetadata entry={stageEntryDocument} exportConfig={exportConfig} />}
        </div>
      );
    }

    return (
      <div>
        <SectionConfigurationContext.Provider value={sectionConfiguration}>
          <EditorPropertiesContext.Provider
            value={editorPropertiesContextForPrintView(this.props.id, Boolean(exportConfig))}
          >
            <div className="printable-page-container">
              <StageEntryNoteView
                beforeEntry={beforeEntry}
                fileId={this.props.id}
                humanId={stageEntryDocument.humanId}
                fileType={ModelTypeConstants.STAGE_ENTRY}
                note={stageEntryDocument.entryVersion.note}
                parentType="Entry"
                title={stageEntryDocument.name}
                isLatest={isLatest}
                permissions={stageEntryDocument.permissions}
                // Layout for print
                showTimestamps={false}
                indexSlice={this.props.indexSlice}
                disableFormatting={true}
                hideTitle={!showTitle}
                locked={true}
                allowEdits={false}
                preview={{showOpenLink: false}}
                viewer={true}
                restoreState={() => {}}
                storeState={() => {}}
                // Display Options
                printOptions={[]}
                printLayout={false}
                showPrintMargins={false}
                spellCheck={false}
                showTableOfContents={false}
              />
            </div>
            {stageEntryDocument.entryVersion.entryProtocols
              .filter((protocol) => shouldRender(protocol))
              .map((protocol) => (
                <div key={protocol.cid} className="printable-page-container">
                  <ProtocolEditor
                    fileId={this.props.id}
                    protocol={protocol}
                    locked={true}
                    storeState={() => {}}
                    restoreState={() => {}}
                    requestSelection={() => {}}
                    viewer={true}
                    showPrintMargins={false}
                  />
                </div>
              ))}
          </EditorPropertiesContext.Provider>
        </SectionConfigurationContext.Provider>
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <PrintableWrapper
        title={
          this.props.editorState.stageEntryDocument != null
            ? this.props.editorState.stageEntryDocument.name
            : undefined
        }
      >
        {this.renderEditor()}
      </PrintableWrapper>
    );
  }
}

export default StageEntryPrintViewConnector.connect(StageEntryPrintView);
